import { DialogComponent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Collects all cart-items-component section IDs on the page.
 * @returns {string[]}
 */
const getCartSectionIds = () => {
  const ids = new Set();

  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  }

  return Array.from(ids);
};

/**
 * Two-step gift wrapping dialog.
 *
 * Step 1: User selects a gift box product from a grid.
 * Step 2: User assigns bottles from the cart to the box using quantity selectors.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} stepTab1 - Step 1 tab button.
 * @property {HTMLButtonElement} stepTab2 - Step 2 tab button.
 * @property {HTMLElement} step1Panel - Step 1 content panel.
 * @property {HTMLElement} step2Panel - Step 2 content panel.
 * @property {HTMLElement} selectedBoxInfo - Text showing the selected box name.
 * @property {HTMLElement} bottlesGrid - Container for bottle cards.
 * @property {HTMLElement} remainingText - Text showing remaining slot count.
 * @property {HTMLButtonElement} addToCartButton - "Add to cart" button.
 * @property {HTMLElement[]} bottleCards - Array of bottle card elements.
 * @property {HTMLButtonElement[]} productCards - Array of product card buttons.
 *
 * @extends {DialogComponent}
 */
class CartGiftWrappingDialog extends DialogComponent {
  requiredRefs = ['dialog', 'stepTab1', 'stepTab2', 'step1Panel', 'step2Panel'];

  /** @type {string | null} */
  #selectedVariantId = null;

  /** @type {number} */
  #selectedPackageContents = 0;

  /** @type {string} */
  #selectedProductTitle = '';

  /** @type {Map<string, number>} variantId → assigned quantity */
  #bottleQuantities = new Map();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Opens the dialog and resets to step 1.
   */
  showDialog() {
    this.#resetState();
    super.showDialog();
  }

  /**
   * Navigate to a step.
   * @param {number} step - Step number (1 or 2).
   */
  goToStep(step) {
    const { stepTab1, stepTab2, step1Panel, step2Panel } = this.refs;

    if (step === 1) {
      stepTab1.classList.add('gift-wrap__step--active');
      stepTab1.setAttribute('aria-selected', 'true');
      stepTab2.classList.remove('gift-wrap__step--active');
      stepTab2.setAttribute('aria-selected', 'false');
      step1Panel.hidden = false;
      step2Panel.hidden = true;
    } else if (step === 2 && this.#selectedVariantId) {
      stepTab1.classList.remove('gift-wrap__step--active');
      stepTab1.setAttribute('aria-selected', 'false');
      stepTab2.classList.add('gift-wrap__step--active');
      stepTab2.setAttribute('aria-selected', 'true');
      stepTab2.disabled = false;
      step1Panel.hidden = true;
      step2Panel.hidden = false;
    }
  }

  /**
   * Handles selecting a gift box product.
   * @param {Event} event
   */
  selectGiftBox(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    if (!button) return;

    // Remove previous selection highlight
    for (const card of this.refs.productCards || []) {
      card.classList.remove('gift-wrap__product-card--selected');
      card.setAttribute('aria-pressed', 'false');
    }

    // Highlight selected card
    button.classList.add('gift-wrap__product-card--selected');
    button.setAttribute('aria-pressed', 'true');

    this.#selectedVariantId = button.dataset.variantId || null;
    this.#selectedPackageContents = parseInt(button.dataset.packageContents || '1', 10);
    this.#selectedProductTitle = button.dataset.productTitle || '';

    // Reset bottle quantities
    this.#bottleQuantities.clear();

    // Wijn.com (9-10-2026): vooraf invullen als de keuze eenduidig is: één wijn in de wagen,
    // of precies zoveel flessen als er in de kist passen. Anders kiest de klant zelf.
    const kaarten = Array.from(this.querySelectorAll('.gift-wrap__bottle-card')).filter(
      (k) => k instanceof HTMLElement && k.dataset.variantId
    );
    const beschikbaar = kaarten.reduce((som, k) => som + parseInt(k.dataset.maxQuantity || '0', 10), 0);
    if (kaarten.length === 1 || beschikbaar === this.#selectedPackageContents) {
      let over = this.#selectedPackageContents;
      for (const k of kaarten) {
        const n = Math.min(parseInt(k.dataset.maxQuantity || '0', 10), over);
        if (n > 0) {
          this.#bottleQuantities.set(String(k.dataset.variantId), n);
          over -= n;
        }
      }
    }

    this.#updateStep2UI();
    this.goToStep(2);
  }

  /**
   * Increments the bottle count for a variant.
   * @param {number} variantId
   */
  incrementBottle(variantId) {
    const key = String(variantId);
    const current = this.#bottleQuantities.get(key) || 0;
    const totalAssigned = this.#getTotalAssigned();

    if (totalAssigned >= this.#selectedPackageContents) return;

    const card = this.querySelector(`.gift-wrap__bottle-card[data-variant-id="${key}"]`);
    const maxQty = parseInt(card?.dataset.maxQuantity || '0', 10);

    if (current >= maxQty) return;

    this.#bottleQuantities.set(key, current + 1);
    this.#updateStep2UI();
  }

  /**
   * Decrements the bottle count for a variant.
   * @param {number} variantId
   */
  decrementBottle(variantId) {
    const key = String(variantId);
    const current = this.#bottleQuantities.get(key) || 0;

    if (current <= 0) return;

    this.#bottleQuantities.set(key, current - 1);
    this.#updateStep2UI();
  }

  /**
   * Adds the gift box to the cart with bottle assignment properties.
   */
  async handleAddToCart() {
    if (!this.#selectedVariantId) return;

    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;

    addToCartButton.disabled = true;
    const originalText = addToCartButton.textContent?.trim() || '';
    addToCartButton.textContent = Theme.translations.adding || 'Adding...';

    try {
      const properties = this.#buildProperties();
      const sectionIds = getCartSectionIds();
      const formData = new FormData();

      formData.append('id', this.#selectedVariantId);
      formData.append('quantity', '1');
      formData.append('sections', sectionIds.join(','));

      for (const [key, value] of Object.entries(properties)) {
        formData.append(`properties[${key}]`, value);
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const data = await response.json();

      if (data.status) {
        addToCartButton.textContent = originalText;
        addToCartButton.disabled = false;
        return;
      }

      // Dispatch cart update event
      this.dispatchEvent(
        new CartAddEvent({}, this.sectionId, {
          source: 'cart-gift-wrapping',
          sections: data.sections,
        })
      );

      // Morph sections
      for (const sectionId of sectionIds) {
        if (data.sections?.[sectionId]) {
          morphSection(sectionId, data.sections[sectionId]);
        }
      }

      // Close the dialog
      this.closeDialog();
    } catch (error) {
      addToCartButton.textContent = originalText;
      addToCartButton.disabled = false;
    }
  }

  /**
   * Gets the section id from the data attribute.
   * @returns {string}
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  /**
   * Resets the dialog state to its initial values.
   */
  #resetState() {
    this.#selectedVariantId = null;
    this.#selectedPackageContents = 0;
    this.#selectedProductTitle = '';
    this.#bottleQuantities.clear();

    // Remove product card selection
    for (const card of this.refs.productCards || []) {
      card.classList.remove('gift-wrap__product-card--selected');
      card.setAttribute('aria-pressed', 'false');
    }

    // Reset step 2 tab
    const { stepTab2 } = this.refs;

    if (stepTab2) {
      stepTab2.disabled = true;
    }

    // Always start on step 1
    this.goToStep(1);
  }

  /**
   * Builds line item properties from bottle assignments.
   * @returns {Record<string, string>}
   */
  #buildProperties() {
    /** @type {Record<string, string>} */
    const properties = {};
    let slotIndex = 1;

    for (const [variantId, qty] of this.#bottleQuantities) {
      if (qty <= 0) continue;

      const card = this.querySelector(`.gift-wrap__bottle-card[data-variant-id="${variantId}"]`);
      const title = card?.dataset.productTitle || '';

      for (let i = 0; i < qty; i++) {
        properties[`Fles ${slotIndex}`] = title;
        properties[`_slot_${slotIndex}_variant_id`] = variantId;
        slotIndex++;
      }
    }

    return properties;
  }

  /**
   * Calculates the total number of assigned bottles.
   * @returns {number}
   */
  #getTotalAssigned() {
    let total = 0;

    for (const qty of this.#bottleQuantities.values()) {
      total += qty;
    }

    return total;
  }

  /**
   * Updates all dynamic UI elements in step 2.
   */
  #updateStep2UI() {
    const { selectedBoxInfo, remainingText, addToCartButton } = this.refs;
    const totalAssigned = this.#getTotalAssigned();
    const remaining = this.#selectedPackageContents - totalAssigned;

    // Update selected box info
    if (selectedBoxInfo) {
      const label = selectedBoxInfo.dataset.label || 'Gekozen verpakking:';
      selectedBoxInfo.textContent = `${label} ${this.#selectedProductTitle}`;
    }

    // Update quantity displays and badges
    const bottleCards = this.querySelectorAll('.gift-wrap__bottle-card');

    for (const card of bottleCards) {
      if (!(card instanceof HTMLElement)) continue;

      const variantId = card.dataset.variantId || '';
      const qty = this.#bottleQuantities.get(variantId) || 0;
      const maxQty = parseInt(card.dataset.maxQuantity || '0', 10);

      // Update quantity display
      const qtyEl = card.querySelector('[data-qty-for]');

      if (qtyEl) {
        qtyEl.textContent = String(qty);
      }

      // Update badge — shows how many are still available (not yet assigned)
      const badge = card.querySelector('.gift-wrap__bottle-badge');

      if (badge instanceof HTMLElement) {
        const availableCount = maxQty - qty;
        badge.textContent = availableCount > 0 ? `${availableCount}x` : '';
        badge.hidden = availableCount <= 0;
      }

      // Disable increment if at max or no remaining slots
      const incrementBtn = card.querySelector('[data-action="increment"]');

      if (incrementBtn instanceof HTMLButtonElement) {
        incrementBtn.disabled = qty >= maxQty || remaining <= 0;
      }

      // Disable decrement if at 0
      const decrementBtn = card.querySelector('[data-action="decrement"]');

      if (decrementBtn instanceof HTMLButtonElement) {
        decrementBtn.disabled = qty <= 0;
      }
    }

    // Update remaining text
    if (remainingText) {
      const template = remaining > 0
        ? (remainingText.dataset.templateRemaining || '')
        : (remainingText.dataset.templateFull || '');

      remainingText.textContent = template.replace('__COUNT__', String(remaining));
    }

    // Enable/disable add to cart button
    if (addToCartButton) {
      addToCartButton.disabled = totalAssigned <= 0;
    }
  }

  /**
   * Handles external cart updates to re-render this section.
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    const detail = /** @type {any} */ (event).detail;
    const sectionHtml = detail?.data?.sections?.[this.sectionId];

    if (sectionHtml) {
      morphSection(this.sectionId, sectionHtml);
    }
  };
}

if (!customElements.get('cart-gift-wrapping-dialog')) {
  customElements.define('cart-gift-wrapping-dialog', CartGiftWrappingDialog);
}
